import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent, Mark, mergeAttributes } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Divider } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import TitleIcon from "@mui/icons-material/Title";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import { parse, serialize, SEMANTIC_KINDS } from "./markdown";

// The site's generic rich-text tool (a system tool, owned by no game): a WYSIWYG over the markdown subset of
// markdown.js. `value` in / `onChange(markdown)` out; the user never sees a symbol. Semantic marks (positive /
// negative / highlight) are the only "colours" - each renderer maps them to its own palette.
const Semantic = Mark.create({
    name: "semantic",
    addAttributes() { return { kind: { default: "highlight", parseHTML: el => el.getAttribute("data-kind"), renderHTML: a => ({ "data-kind": a.kind, class: `rt-${a.kind}` }) } }; },
    parseHTML() { return [{ tag: "span[data-kind]" }]; },
    renderHTML({ HTMLAttributes }) { return ["span", mergeAttributes(HTMLAttributes), 0]; },
    addCommands() {
        return {
            toggleSemantic: kind => ({ commands, editor }) => editor.isActive("semantic", { kind }) ? commands.unsetMark("semantic") : commands.setMark("semantic", { kind }),
        };
    },
});

const extensions = [
    StarterKit.configure({
        heading: { levels: [1, 2] },
        link: false, underline: false, code: false, codeBlock: false, blockquote: false, strike: false,
    }),
    Semantic,
];

const SEMANTIC_LABEL = { positive: "Positive", negative: "Negative", highlight: "Highlight" };

export default function RichTextEditor({ value, onChange, minHeight = 160, placeholder }) {
    const lastEmitted = useRef(value ?? "");
    const editor = useEditor({
        extensions,
        content: parse(value ?? ""),
        immediatelyRender: true,
        onUpdate: ({ editor }) => {
            const md = serialize(editor.getJSON());
            lastEmitted.current = md;
            onChange?.(md);
        },
    });

    // An external value (another item opened) replaces the document; our own edits do not bounce back
    useEffect(() => {
        if (!editor || (value ?? "") === lastEmitted.current) return;
        lastEmitted.current = value ?? "";
        editor.commands.setContent(parse(value ?? ""), { emitUpdate: false });
    }, [editor, value]);

    if (!editor) return null;
    const active = [];
    if (editor.isActive("heading", { level: 1 })) active.push("h1");
    if (editor.isActive("heading", { level: 2 })) active.push("h2");
    if (editor.isActive("bold")) active.push("bold");
    if (editor.isActive("italic")) active.push("italic");
    if (editor.isActive("bulletList")) active.push("bullet");
    if (editor.isActive("orderedList")) active.push("ordered");
    SEMANTIC_KINDS.forEach(k => { if (editor.isActive("semantic", { kind: k })) active.push(k); });
    const run = fn => () => { fn(editor.chain().focus()).run(); };

    return (
        <Box className="rt-editor" sx={{ border: "1px solid rgba(0,0,0,0.23)", borderRadius: 1, "&:focus-within": { borderColor: "primary.main" } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 0.5, borderBottom: "1px solid rgba(0,0,0,0.12)", flexWrap: "wrap" }}>
                <ToggleButtonGroup size="small" value={active}>
                    <Tooltip title="Title"><ToggleButton value="h1" onClick={run(c => c.toggleHeading({ level: 1 }))}><TitleIcon fontSize="small" /></ToggleButton></Tooltip>
                    <Tooltip title="Header"><ToggleButton value="h2" onClick={run(c => c.toggleHeading({ level: 2 }))}><span style={{ fontWeight: 700, fontSize: 13 }}>H</span></ToggleButton></Tooltip>
                    <Tooltip title="Bold"><ToggleButton value="bold" onClick={run(c => c.toggleBold())}><FormatBoldIcon fontSize="small" /></ToggleButton></Tooltip>
                    <Tooltip title="Italic"><ToggleButton value="italic" onClick={run(c => c.toggleItalic())}><FormatItalicIcon fontSize="small" /></ToggleButton></Tooltip>
                    <Tooltip title="Bullet list"><ToggleButton value="bullet" onClick={run(c => c.toggleBulletList())}><FormatListBulletedIcon fontSize="small" /></ToggleButton></Tooltip>
                    <Tooltip title="Numbered list"><ToggleButton value="ordered" onClick={run(c => c.toggleOrderedList())}><FormatListNumberedIcon fontSize="small" /></ToggleButton></Tooltip>
                    <Tooltip title="Rule"><ToggleButton value="rule" onClick={run(c => c.setHorizontalRule())}><HorizontalRuleIcon fontSize="small" /></ToggleButton></Tooltip>
                </ToggleButtonGroup>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <ToggleButtonGroup size="small" value={active}>
                    {SEMANTIC_KINDS.map(k => (
                        <Tooltip key={k} title={SEMANTIC_LABEL[k]}>
                            <ToggleButton value={k} onClick={run(c => c.toggleSemantic(k))}><span className={`rt-${k}`} style={{ fontWeight: 700, fontSize: 12, padding: "0 4px" }}>{SEMANTIC_LABEL[k]}</span></ToggleButton>
                        </Tooltip>
                    ))}
                </ToggleButtonGroup>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title="Clear formatting"><ToggleButton size="small" value="clear" onClick={run(c => c.unsetAllMarks().clearNodes())}><FormatClearIcon fontSize="small" /></ToggleButton></Tooltip>
            </Box>
            <Box className="rt-body" sx={{ minHeight, p: 1.5, "& .ProseMirror": { outline: "none", minHeight: minHeight - 24 }, "& .ProseMirror p.is-editor-empty:first-of-type::before": { content: `"${placeholder ?? ""}"`, color: "text.disabled", float: "left", pointerEvents: "none", height: 0 } }}>
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
}
